import { eDeColega, type NivelDeAcesso } from '@/api/client';
import { useAuthOpcional } from '@/context/AuthProvider';
import { useFeatureLiberada } from '@/hooks/useSubscription';
import { useAtuacao } from '@/lib/delegacao/atuacao';

export interface AcoesNoProcesso {
  /** O processo é de um colega (a visão do escritório; nunca na delegação). */
  doColega: boolean;
  /** Abre o Peticionar: no próprio, sempre; no do colega, para protocolar ou preparar. */
  podePeticionar: boolean;
  /** No do colega, quem não protocola prepara a petição na conta do dono, que a protocola. */
  preparaParaODono: boolean;
  rotuloPeticionar: string;
  /** O mesmo, com o verbo dos autos: "Protocolar". */
  rotuloProtocolar: string;
  /** O Chat de IA do processo: no do colega, em Colaboração (o plano e a cota são do dono). */
  ia: boolean;
}

/**
 * O que se faz com a petição num processo (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md A3, 3b e 3c).
 *
 * No processo de um colega, o advogado com a própria assinatura peticiona com o próprio
 * certificado e a própria OAB (o Peticionar grava o processo no acervo dele). Quem não
 * protocola — o estagiário, quem não tem OAB, ou quem entra pela assinatura do escritório —
 * prepara a petição em Colaboração: ela nasce na conta do dono, "preparada por" quem a montou,
 * e o dono a protocola. Em Leitura, nenhum dos dois.
 */
/** A parte do Peticionar (sem a assinatura): também para o menu da lista do acervo. */
export function usePeticionarNoProcesso(
  registro: { acesso?: NivelDeAcesso } | null | undefined,
): Omit<AcoesNoProcesso, 'ia'> {
  // Opcional: o menu da lista do acervo também usa, renderizado fora do provider nos testes.
  const user = useAuthOpcional()?.user;
  const atuacao = useAtuacao();
  const doColega = !atuacao && !!registro && eDeColega(registro);
  const colabora = !doColega || registro?.acesso === 'COLABORACAO';
  const protocola = user?.role === 'advogado';
  const preparaParaODono = doColega && colabora && !protocola;
  return {
    doColega,
    podePeticionar: colabora,
    preparaParaODono,
    rotuloPeticionar: preparaParaODono ? 'Preparar petição' : 'Peticionar',
    // Nos autos a ação é "Protocolar" (docs/PLANO_BANCADA.md): a peça se escreve
    // no editor dali, e o que falta é enviá-la. Quem prepara para o titular não
    // protocola — segue "Preparar petição".
    rotuloProtocolar: preparaParaODono ? 'Preparar petição' : 'Protocolar',
  };
}

export function useAcoesNoProcesso(registro: { acesso?: NivelDeAcesso } | null | undefined): AcoesNoProcesso {
  const peticionar = usePeticionarNoProcesso(registro);
  const iaLiberada = useFeatureLiberada('peticao_ia');
  const colabora = !peticionar.doColega || registro?.acesso === 'COLABORACAO';
  return { ...peticionar, ia: peticionar.doColega ? colabora : iaLiberada };
}
